import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import TextArea from '../../../components/TextArea';
import { getPositionNameError, getPositionDescriptionError } from '../../../utils/validation';

function AddPositionModal({ open, onClose, onConfirm, positionData, groups, editMode = false }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    groupId: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when dialog opens or when positionData changes
  useEffect(() => {
    if (open) {
      if (editMode && positionData) {
        setFormData({
          id: positionData.id,
          code: positionData.code || '',
          name: positionData.name || '',
          description: positionData.description || '',
          groupId: positionData.groupId || '',
        });
      } else {
        setFormData({
          code: '',
          name: '',
          description: '',
          groupId: '',
        });
      }
      setErrors({});
    }
  }, [open, positionData, editMode]);

  // Handle input change
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'name') {
      const nameError = getPositionNameError(value, t);
      if (nameError) {
        newErrors.name = nameError;
      } else {
        delete newErrors.name;
      }
    } else if (field === 'description') {
      const descriptionError = getPositionDescriptionError(value, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.name.trim() !== '' && formData.groupId !== '';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('positions.addDialog.nameRequired');
    } else {
      const nameError = getPositionNameError(formData.name, t);
      if (nameError) newErrors.name = nameError;
    }

    const descriptionError = getPositionDescriptionError(formData.description, t);
    if (descriptionError) newErrors.description = descriptionError;

    if (!formData.groupId) {
      newErrors.groupId = t('positions.addDialog.groupRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(formData);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: editMode ? 'primary.main' : 'success.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('positions.editDialog.title') : t('positions.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {/* Code */}
          <Input
            label={t('positions.addDialog.code')}
            value={formData.code}
            onChange={(e) => handleChange('code', e.target.value)}
            placeholder={t('positions.addDialog.code')}
            id="code"
            name="code"
            data-testid="position-modal-input-code"
          />

          {/* Name */}
          <Input
            label={t('positions.addDialog.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            error={!!errors.name}
            helperText={errors.name}
            placeholder={t('positions.addDialog.name')}
            id="name"
            name="name"
            data-testid="position-modal-input-name"
          />

          {/* Description */}
          <TextArea
            label={t('positions.addDialog.description')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            placeholder={t('positions.addDialog.description')}
            error={!!errors.description}
            helperText={errors.description}
            id="description"
            name="description"
            data-testid="position-modal-input-description"
          />

          {/* Officer Group */}
          <Select
            label={t('positions.addDialog.group')}
            value={formData.groupId}
            onChange={(e) => handleChange('groupId', e.target.value)}
            options={groups.map(g => ({ value: g.id, label: g.name }))}
            required
            error={!!errors.groupId}
            helperText={errors.groupId}
            id="groupId"
            name="groupId"
            data-testid="position-modal-select-groupId"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="position-modal-button-cancel"
        >
          {t('positions.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="position-modal-button-confirm"
        >
          {editMode ? t('positions.editDialog.confirm') : t('positions.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddPositionModal;
